import { Fragment, memo, useEffect, useState } from 'react';
import { useRouter } from '~/utils/navigation';
import { useSelector } from 'react-redux';
import { numGrouping } from '@comeon/utility';
import { trackHeaderEvent } from '@comeon/analytics';
import { Box } from '@comeon/ui';
import { ActivityTimer } from '~/components/account/async.js';
import Icon, {
	ICON_STOPWATCH_LINE,
	ICON_QUICK_DEPOSIT_COINS,
	ICON_QUICK_DEPOSIT_LIGHTNING_NEW,
	ICON_DIAMOND_LINE
} from '~/components/icon';
import Anchor from '../misc/anchor.js';
import { Bigger, Smaller } from '~/components/layout/mediaquery.js';
import { ROUTE_SHOP } from '~/components/router/constants.js';
import { Balance } from '~/components/balance';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import useTranslations from '~/components/translation/use-translations.ts';
import { QuickDepositLink } from '~/components/header/right-container-header-buttons.js';
import { selectShowDetailedBalance } from '../brand/ducks/selectors.js';
import Loadable from '@comeon/loadable';
import NullLoader from '../null-loader.js';
import useGlobalQuickDeposit from '../../hooks/use-global-quick-deposit';
import styled from 'styled-components';

const StopwatchFill = () => <Icon type={ICON_STOPWATCH_LINE} />;

const textKeys = [
	'RESPONSIVE_ACCOUNT_ICON',
	'MOBILE_HEADER_BAR_DEPOSIT',
	'AVAILABLE_BALANCE_CASINO',
	'AVAILABLE_BALANCE_SPORTSBOOK',
	'TOTAL_BALANCE',
	'MORE_INFO_BALANCE_CTA',
	'MORE_INFO_BALANCE_POPUP_TITLE',
	'MORE_INFO_BALANCE_POPUP_DESC',
	'MORE_INFO_BALANCE_POPUP_DESC_2',
	'MORE_INFO_BALANCE_POPUP_CONFIRM'
];

const StyledIcon = styled(Icon)`
	height: ${props => (props.big ? '24px !important' : '')};
	width: ${props => (props.big ? '24px !important' : '')};
`;

const DetailedBalanceDropDown = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "dega-bundle" */
			'./detailed-balance-dropdown'
		),
	loading: NullLoader
});

const HeaderBalance = ({
	bonusPoints,
	balances,
	hydrated,
	isDanishFranchise,
	isShopEnabled,
	isInGame,
	dataAtBalance,
	isEventLive
}) => {
	const router = useRouter();
	const contextualPathTransformer = useContextualPathTransformer();
	const [isSmallerScreen, setSmallerScreen] = useState(window.matchMedia('(max-width: 318px)').matches);
	const [isPortrait, setPortrait] = useState(window.matchMedia('(orientation: portrait)').matches);
	const { translations } = useTranslations(textKeys);
	const showDetailedBalance = useSelector(selectShowDetailedBalance);
	const isDepositBlocked = useSelector(state => state.account.isDepositBlocked);
	const { isQuickDepositAvailable } = useGlobalQuickDeposit(false);

	useEffect(() => {
		const resizeHandler = () => {
			setSmallerScreen(window?.matchMedia('(max-width: 318px)')?.matches);
			setPortrait(window?.matchMedia('(orientation: portrait)')?.matches);
		};

		window.removeEventListener('resize', resizeHandler);
		window.addEventListener('resize', resizeHandler);
		// FIXME obvious bug
		return () => {
			window.removeEventListener('resize', resizeHandler);
		};
	}, [isSmallerScreen, isPortrait]);

	const inGameView = isInGame === 'true';

	const isInGamePortrait = inGameView && isPortrait;

	useEffect(() => {
		// dispatch it once mounted
		setTimeout(() => {
			window.dispatchEvent(new Event('resize'));
		}, 1500);
	}, []);

	return (
		<Fragment>
			{hydrated && (
				<Bigger
					render={() => (
						<>
							{isDanishFranchise && (
								<Box mr={6}>
									<Balance
										title={<ActivityTimer className="danish-session-timer--regulation-header" />}
										icon={<StopwatchFill />}
									/>
								</Box>
							)}
							{isShopEnabled && (
								<Box mr={5}>
									<Anchor
										onClick={() => {
											router.push(contextualPathTransformer(ROUTE_SHOP));
											trackHeaderEvent('Shop icon');
										}}
										data-at="points-shop-header"
									>
										<Balance
											title={(bonusPoints && numGrouping(bonusPoints, false)) || 0}
											icon={<Icon type={ICON_DIAMOND_LINE} />}
										/>
									</Anchor>
								</Box>
							)}
							<Box mr={5}>
								{showDetailedBalance ? (
									<DetailedBalanceDropDown balances={balances} translations={translations} />
								) : (
									<QuickDepositLink>
										<Balance
											dataAt={dataAtBalance}
											title={balances.totalBalance || 0}
											label={translations.MOBILE_HEADER_BAR_DEPOSIT}
											disableLabel={isEventLive || isDepositBlocked}
										/>
									</QuickDepositLink>
								)}
							</Box>
						</>
					)}
				/>
			)}
			<Smaller
				render={() =>
					!isInGamePortrait ? (
						<>
							{isShopEnabled && !isSmallerScreen && (
								<Box mr={2}>
									<Anchor
										onClick={() => {
											router.push(contextualPathTransformer(ROUTE_SHOP));
											trackHeaderEvent('Shop icon');
										}}
										data-at="points-shop-header"
									>
										<Balance
											small
											title={(bonusPoints && numGrouping(bonusPoints, false)) || 0}
											icon={<Icon type={ICON_DIAMOND_LINE} />}
											disableLabel={isEventLive}
										/>
									</Anchor>
								</Box>
							)}
							<Box mr={2}>
								{showDetailedBalance ? (
									<DetailedBalanceDropDown balances={balances} translations={translations} />
								) : (
									<QuickDepositLink>
										<Balance
											title={balances.totalBalance || 0}
											icon={
												<StyledIcon
													big={isQuickDepositAvailable}
													type={
														isQuickDepositAvailable
															? ICON_QUICK_DEPOSIT_LIGHTNING_NEW
															: ICON_QUICK_DEPOSIT_COINS
													}
												/>
											}
											small
											iconprimary
											primary
											disableIcon={isEventLive || isDepositBlocked}
										/>
									</QuickDepositLink>
								)}
							</Box>
						</>
					) : null
				}
			/>
		</Fragment>
	);
};

export default memo(HeaderBalance);
